import {Box, Button, Drawer, Field, HStack, Icon, Input, Portal, Spinner, Text, Textarea, VStack} from "@chakra-ui/react"
import {IoCloseSharp} from "react-icons/io5";
import {useState, useEffect} from "react";
import {showToast} from "@/components/ui/toaster";
import JsonMappingForm, {extractFields} from "./JsonMappingForm";

const ConfigureWorkerModal = ({isOpen, onClose, onSave, worker}) => {
    const [isSaving, setIsSaving] = useState(false);
    const [name, setName] = useState('');
    const [tag, setTag] = useState('');
    const [description, setDescription] = useState('');
    const [jsonInput, setJsonInput] = useState('');
    const [jsonError, setJsonError] = useState('');
    const [parsedFields, setParsedFields] = useState(null);

    useEffect(() => {
        if (isOpen) {
            setName(worker?.name ?? '');
            setTag(worker?.tag ?? '');
            setDescription(worker?.description ?? '');
            setJsonInput(worker?.jsonSchema ?? '');
            setParsedFields(worker?.jsonSchema ? extractFields(JSON.parse(worker.jsonSchema)) : null);
            setJsonError('');
        }
    }, [isOpen, worker]);

    const handleParseJson = () => {
        try {
            const parsed = JSON.parse(jsonInput);
            setParsedFields(extractFields(parsed));
            setJsonError('');
        } catch {
            setJsonError('Nieprawidłowy JSON');
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await onSave?.({ ...(worker ?? {}), name, tag, description, jsonSchema: jsonInput });
            showToast(worker ? "Worker updated" : "Worker created", null, "success");
            onClose();
        } catch {
            showToast("Error", "Failed to save worker", "error");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Drawer.Root open={isOpen} onOpenChange={(e) => !e.open && onClose()} size={'xl'} placement={'end'}>
            <Portal>
                <Drawer.Backdrop/>
                <Drawer.Positioner padding="4">
                    <Drawer.Content minW={'100%'} bg={"bg.drawer"} borderRadius={16}>
                        <Drawer.Header>
                            <HStack justifyContent={'space-between'} w={'full'}>
                                <Text color={'white'}>{worker ? 'Edit Worker' : 'Configure Worker'}</Text>
                                <Icon fontSize={32} color={'text.grey'} onClick={onClose} cursor={'pointer'}>
                                    <IoCloseSharp/>
                                </Icon>
                            </HStack>
                        </Drawer.Header>
                        <Drawer.Body>
                            <VStack w={{sm: 'full', xl: '50%'}} alignItems={'flex-start'} mx={'auto'}>
                                <Field.Root required>
                                    <Field.Label color={'white'} fontSize={18}>
                                        Worker name <Field.RequiredIndicator/>
                                    </Field.Label>
                                    <Input variant={'primary'} placeholder="Enter worker name" value={name} onChange={(e) => setName(e.target.value)}/>
                                </Field.Root>

                                <Field.Root mt={4}>
                                    <Field.Label color={'white'} fontSize={18}>Tag</Field.Label>
                                    <Input variant={'primary'} placeholder="e.g. orders" value={tag} onChange={(e) => setTag(e.target.value)}/>
                                </Field.Root>

                                <Field.Root mt={4}>
                                    <Field.Label color={'white'} fontSize={18}>Description</Field.Label>
                                    <Textarea
                                        variant={'outline'}
                                        placeholder="Enter worker description"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value.slice(0, 100))}
                                        maxLength={100}
                                        resize={'none'}
                                        rows={3}
                                        color={'white'}
                                    />
                                    <Field.HelperText>{description.length}/100</Field.HelperText>
                                </Field.Root>

                                <Field.Root mt={4}>
                                    <Field.Label color={'white'} fontSize={18}>Response JSON</Field.Label>
                                    <Textarea
                                        variant={'outline'}
                                        placeholder="Wklej przykładowy JSON..."
                                        value={jsonInput}
                                        onChange={(e) => setJsonInput(e.target.value)}
                                        resize={'none'}
                                        rows={6}
                                        color={'white'}
                                    />
                                    {jsonError && <Text color="red.400" fontSize={13}>{jsonError}</Text>}
                                    <Button size="sm" mt={2} variant="outlined" onClick={handleParseJson}>Parsuj JSON</Button>
                                </Field.Root>

                                {parsedFields && (
                                    <Box mt={6} w="full">
                                        <Text color="white" fontSize={18} mb={3}>Mapowanie pól</Text>
                                        <JsonMappingForm fields={parsedFields}/>
                                    </Box>
                                )}
                            </VStack>
                        </Drawer.Body>
                        <Drawer.Footer borderRadius={16}>
                            <HStack justifyContent={'flex-end'} w={'full'} gap={3}>
                                <Button variant="outlined" textDecoration='unset' onClick={onClose} disabled={isSaving}>Cancel</Button>
                                <Button variant="primary" textDecoration='unset' onClick={handleSave} disabled={isSaving}>
                                    {isSaving ? <Spinner size="sm"/> : "Save"}
                                </Button>
                            </HStack>
                        </Drawer.Footer>
                    </Drawer.Content>
                </Drawer.Positioner>
            </Portal>
        </Drawer.Root>
    );
};

export default ConfigureWorkerModal;
